<script lang="ts" setup>
import { type FindOrderOut } from '~/types/ecommerce/generated.codegen';

const props = defineProps<{
  order: FindOrderOut;
}>();

const summary = computed(() => props.order.summary.body.items);
const summaryWhithoutTotal = computed(() => summary.value.filter(item => item?.type === 'summary_item'));
const total = computed(() => summary.value.find(item => item?.type === 'summary_final'));
const installments = computed(() => summary.value.find(item => item?.type === 'summary_installments'));

const observation = ref(props.order.notes.internal || '');
</script>

<template>
  <div class="order-payment-method-content">
    <div class="header">
      <span class="title">Método de pagamento</span>
    </div>
    <div class="info-content">
      <span class="payment-method">{{ props.order.payment_method }}</span>
      <SolDivider orientation="horizontal" thickness="x-small" />
      <div class="summary-wrapper">
        <div class="summary">
          <template v-for="(item, index) in summaryWhithoutTotal" :key="item?.title ?? index">
            <div class="summary-item">
              <div class="label-wrapper">
                <span class="summary-item-label">{{ item?.title }}</span>
              </div>
              <span class="summary-item-value" :class="{ 'free-badge': item?.value === 'Grátis' }">{{ item?.value }}</span>
            </div>
          </template>
        </div>
        <div class="total">
          <span class="summary-total-label">Total</span>
          <span class="summary-total-value">{{ total?.value }}</span>
        </div>
        <div v-if="installments" class="installments">
          <span class="installments-label">Parcelas</span>
          <span class="installments-value">{{ installments.value }}</span>
        </div>
      </div>
      <SolDivider orientation="horizontal" thickness="x-small" />
      <div class="obs-container">
        <span class="obs-title">Observações (opcional)</span>
        <SolInputTextarea
          id="obs"
          v-model="observation"
          name="obs"
          disabled
          placeholder="Seu texto aqui"
          class="obs-input"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.order-payment-method-content {
  @apply flex flex-col gap-4xs px-4xs py-2xs md:site:w-[422px];
  @apply border-1 border-neutral-high-medium rounded-lg;

  .header {
    @apply block;

    .title {
      @apply fonts-heading-h3;
    }
  }

  .info-content {
    @apply flex flex-col gap-4xs;

    .payment-method {
      @apply fonts-body-large-regular text-neutral-low-dark;
    }

    .summary-wrapper {
      @apply flex flex-col gap-nano;

      .summary {
        @apply flex flex-col gap-nano;

        .summary-item {
          @apply flex justify-between items-center;

          .label-wrapper {
            @apply flex;

            .summary-item-label {
              @apply fonts-body-medium-regular text-neutral-low-light;
            }
          }

          .summary-item-value {
            @apply fonts-body-medium-regular text-neutral-low-dark;

            &.free-badge {
                @apply text-feedback-positive-pure fonts-body-medium-bold;
              }
          }
        }
      }

      .total {
        @apply flex justify-between items-center;

        .summary-total-label {
          @apply fonts-body-large-bold text-neutral-low-dark;
        }

        .summary-total-value {
          @apply fonts-body-x-large-bold text-neutral-low-dark;
        }
      }

      .installments {
        @apply flex justify-between;

        .installments-label {
          @apply fonts-body-large-regular text-neutral-low-dark;
        }

        .installments-value {
          @apply fonts-body-large-bold text-neutral-low-dark;
        }
      }
    }

    .obs-container {
      @apply flex flex-col gap-quark;

      .obs-title {
        @apply fonts-body-medium-bold text-neutral-high-dark;
      }
    }
  }
}
</style>
